'use client';

import React from 'react';

const TRUST_POINTS = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M10 2L3 5.5V10C3 13.87 6.13 17.5 10 18C13.87 17.5 17 13.87 17 10V5.5L10 2Z" stroke="#1F4D3F" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M7 10L9 12L13 8" stroke="#1F4D3F" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    headline: 'Permission-aware',
    body: 'VINA works within your existing permissions. You control which modules are connected — each permission is explicit and revocable.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="14" height="14" rx="3" stroke="#1F4D3F" strokeWidth="1.3" />
        <path d="M7 10l2 2 4-4" stroke="#1F4D3F" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    headline: 'Business data stays within your control',
    body: 'All data remains within your existing infrastructure. VINA reads — never stores or exports — your business records.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="7" stroke="#1F4D3F" strokeWidth="1.3" />
        <path d="M10 7v3l2 2" stroke="#1F4D3F" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
    headline: 'Built for enterprise environments',
    body: 'Designed around your existing workflows. No new integrations, no data migration, no setup complexity.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4 6h12M4 10h8M4 14h10" stroke="#1F4D3F" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
    headline: 'Designed around your existing workflows',
    body: 'Every query, every data read, every action is logged in a full audit trail. You always know what VINA accessed and when.',
  },
];

export default function TrustControl() {
  return (
    <section
      id="trust"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #F6F5F0 0%, #FBFAF6 100%)' }}
      aria-label="Trust and Control"
    >
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              letterSpacing: '-0.03em',
              color: '#0D2620',
              lineHeight: 1.1,
              marginBottom: '16px',
            }}
          >
            Your business stays yours.
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '1.1rem',
              color: '#3D5550',
              maxWidth: '480px',
              margin: '0 auto',
              lineHeight: 1.65,
            }}
          >
            VINA works within your existing permissions and security boundaries.
          </p>
        </div>

        {/* Trust points */}
        <div className="grid sm:grid-cols-2 gap-5 mb-12">
          {TRUST_POINTS?.map((point, i) => (
            <div
              key={i}
              className="rounded-3xl p-7 transition-all"
              style={{
                background: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(211,223,219,0.7)',
              }}
            >
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: 'rgba(31,77,63,0.07)' }}
              >
                {point?.icon}
              </div>
              <h3
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#0D2620',
                  letterSpacing: '-0.02em',
                  marginBottom: '10px',
                  lineHeight: 1.4,
                }}
              >
                {point?.headline}
              </h3>
              <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: '#4F665F', lineHeight: 1.7 }}>
                {point?.body}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div
          className="rounded-3xl p-8 text-center"
          style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(211,223,219,0.7)' }}
        >
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '15px',
              color: '#1A3530',
              lineHeight: 1.7,
              maxWidth: '560px',
              margin: '0 auto',
            }}
          >
            Intelligence without compromise. VINA is designed for trust from the ground up — because your business data is yours.
          </p>
        </div>
      </div>
    </section>
  );
}
